/**
 * Splits long edges of a result mesh so the FEA displacement shader has enough
 * vertices to bend it.
 *
 * The deformed view moves each vertex by the displacement sampled at that
 * vertex, and the surface between vertices stays straight. A mesh built from
 * coarse CAD (a box tessellated into 12 triangles) has vertices only at its
 * corners, so a plate supported at both ends draws flat however far it bends
 * in the middle. Splitting edges longer than a set length puts vertices along
 * the span. Callers apply it to FEA run results only, whose mesh is the input
 * CAD; optimized result meshes are drawn as the runner wrote them.
 *
 * Each pass bisects every marked edge at its midpoint and splits each triangle
 * by how many of its edges are marked (1 → 2, 2 → 3, 3 → 4 triangles). An edge
 * is marked by its length alone, so both triangles on it split it the same way
 * and no T-junctions appear; edges duplicated across a sharp crease carry the
 * same coordinates and split at the same points. Positions stay on the input
 * surface, and new normals are interpolated from their edge's ends so shading
 * stays as it was.
 */

/** Longest edge, as a share of the mesh's longest side, left unsplit. */
export const DISPLACEMENT_REFINE_SEGMENTS = 64;
/** Most triangles refining may add; a pass that would exceed it is not run. */
export const DISPLACEMENT_REFINE_MAX_ADDED_TRIANGLES = 500_000;
/** Safety stop on bisection passes (each halves the marked edges). */
const MAX_PASSES = 20;

export interface RefinedMesh {
  positions: Float32Array;
  indices: Uint32Array;
  normals: Float32Array;
  /** Triangles added; 0 when the input came through unchanged. */
  addedTriangles: number;
}

/** Edge length above which `refineLongEdges` splits, for a mesh with these positions. */
export function displacementRefineEdgeLength(positions: Float32Array): number {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i], y = positions[i + 1], z = positions[i + 2];
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (z < minZ) minZ = z;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
    if (z > maxZ) maxZ = z;
  }
  const longestSide = Math.max(maxX - minX, maxY - minY, maxZ - minZ);
  return Number.isFinite(longestSide) && longestSide > 0 ? longestSide / DISPLACEMENT_REFINE_SEGMENTS : Infinity;
}

/**
 * Splits edges longer than `maxEdgeLength` until none are left, or until the
 * next pass would add more than `maxAddedTriangles` in total. Returns the input
 * arrays themselves when nothing needs splitting.
 */
export function refineLongEdges(
  positions: Float32Array,
  indices: Uint32Array,
  normals: Float32Array,
  maxEdgeLength: number,
  maxAddedTriangles: number = DISPLACEMENT_REFINE_MAX_ADDED_TRIANGLES
): RefinedMesh {
  const startTriangles = indices.length / 3;
  if (!(maxEdgeLength > 0) || !Number.isFinite(maxEdgeLength)) {
    return { positions, indices, normals, addedTriangles: 0 };
  }
  const maxLengthSq = maxEdgeLength * maxEdgeLength;

  for (let pass = 0; pass < MAX_PASSES; pass++) {
    const triangleCount = indices.length / 3;
    const marks = new Uint8Array(triangleCount);
    let splitEdgeSlots = 0;
    for (let t = 0; t < triangleCount; t++) {
      const a = indices[t * 3], b = indices[t * 3 + 1], c = indices[t * 3 + 2];
      let mask = 0;
      if (lengthSq(positions, a, b) > maxLengthSq) mask |= 1;
      if (lengthSq(positions, b, c) > maxLengthSq) mask |= 2;
      if (lengthSq(positions, c, a) > maxLengthSq) mask |= 4;
      marks[t] = mask;
      splitEdgeSlots += (mask & 1) + ((mask >> 1) & 1) + ((mask >> 2) & 1);
    }
    if (splitEdgeSlots === 0) break;
    // Each marked edge on a triangle adds one triangle.
    if (triangleCount + splitEdgeSlots - startTriangles > maxAddedTriangles) break;

    const vertexCount = positions.length / 3;
    // Every marked edge is shared by at most the triangles that marked it, so
    // `splitEdgeSlots` bounds the new vertices.
    const nextPositions = new Float32Array((vertexCount + splitEdgeSlots) * 3);
    const nextNormals = new Float32Array((vertexCount + splitEdgeSlots) * 3);
    nextPositions.set(positions);
    nextNormals.set(normals);
    const nextIndices = new Uint32Array((triangleCount + splitEdgeSlots) * 3);
    const midpointOf = new Map<number, number>();
    let nextVertex = vertexCount;
    let out = 0;

    const midpoint = (p: number, q: number): number => {
      const key = p < q ? p * vertexCount + q : q * vertexCount + p;
      const known = midpointOf.get(key);
      if (known !== undefined) return known;
      const m = nextVertex++;
      for (let k = 0; k < 3; k++) {
        nextPositions[m * 3 + k] = (positions[p * 3 + k] + positions[q * 3 + k]) * 0.5;
      }
      const nx = normals[p * 3] + normals[q * 3];
      const ny = normals[p * 3 + 1] + normals[q * 3 + 1];
      const nz = normals[p * 3 + 2] + normals[q * 3 + 2];
      const length = Math.hypot(nx, ny, nz);
      if (length > 0) {
        nextNormals[m * 3] = nx / length;
        nextNormals[m * 3 + 1] = ny / length;
        nextNormals[m * 3 + 2] = nz / length;
      } else {
        nextNormals[m * 3] = normals[p * 3];
        nextNormals[m * 3 + 1] = normals[p * 3 + 1];
        nextNormals[m * 3 + 2] = normals[p * 3 + 2];
      }
      midpointOf.set(key, m);
      return m;
    };
    const emit = (a: number, b: number, c: number): void => {
      nextIndices[out++] = a;
      nextIndices[out++] = b;
      nextIndices[out++] = c;
    };

    const v = new Uint32Array(3);
    for (let t = 0; t < triangleCount; t++) {
      v[0] = indices[t * 3];
      v[1] = indices[t * 3 + 1];
      v[2] = indices[t * 3 + 2];
      const mask = marks[t];
      if (mask === 0) {
        emit(v[0], v[1], v[2]);
      } else if (mask === 7) {
        const m0 = midpoint(v[0], v[1]);
        const m1 = midpoint(v[1], v[2]);
        const m2 = midpoint(v[2], v[0]);
        emit(v[0], m0, m2);
        emit(m0, v[1], m1);
        emit(m2, m1, v[2]);
        emit(m0, m1, m2);
      } else if (mask === 1 || mask === 2 || mask === 4) {
        // Rotate so the marked edge runs a → b.
        const s = mask === 1 ? 0 : mask === 2 ? 1 : 2;
        const a = v[s], b = v[(s + 1) % 3], c = v[(s + 2) % 3];
        const m = midpoint(a, b);
        emit(a, m, c);
        emit(m, b, c);
      } else {
        // Two marked edges: rotate so the unmarked one runs c → a.
        const unmarked = (mask & 1) === 0 ? 0 : (mask & 2) === 0 ? 1 : 2;
        const s = (unmarked + 1) % 3;
        const a = v[s], b = v[(s + 1) % 3], c = v[(s + 2) % 3];
        const m0 = midpoint(a, b);
        const m1 = midpoint(b, c);
        emit(m0, b, m1);
        // The quad a, m0, m1, c is cut along its shorter diagonal.
        if (lengthSq(nextPositions, a, m1) <= lengthSq(nextPositions, m0, c)) {
          emit(a, m0, m1);
          emit(a, m1, c);
        } else {
          emit(a, m0, c);
          emit(m0, m1, c);
        }
      }
    }

    positions = nextPositions.slice(0, nextVertex * 3);
    normals = nextNormals.slice(0, nextVertex * 3);
    indices = nextIndices;
  }

  return { positions, indices, normals, addedTriangles: indices.length / 3 - startTriangles };
}

function lengthSq(positions: Float32Array, p: number, q: number): number {
  const dx = positions[p * 3] - positions[q * 3];
  const dy = positions[p * 3 + 1] - positions[q * 3 + 1];
  const dz = positions[p * 3 + 2] - positions[q * 3 + 2];
  return dx * dx + dy * dy + dz * dz;
}
